import type React from 'react'

interface ScoreCardProps {
  title: string
  score?: number
  category?: string
  description?: string
  icon?: React.ReactNode
  type?: 'awareness' | 'sentiment' | 'credibility'
  className?: string
}

// Circular progress component
function CircularProgress({ score, className = '' }: { score: number; className?: string }) {
  const circumference = 2 * Math.PI * 20 // radius of 20
  const strokeDasharray = circumference
  const strokeDashoffset = circumference - (score / 100) * circumference

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#22c55e' // green-500
    if (score >= 60) return '#eab308' // yellow-500
    if (score >= 40) return '#f97316' // orange-500
    return '#ef4444' // red-500
  }

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg className="h-16 w-16 -rotate-90 transform" width="64" height="64">
        {/* Background circle */}
        <circle
          cx="32"
          cy="32"
          r="20"
          stroke="#e5e7eb"
          strokeWidth="4"
          fill="transparent"
          className="opacity-20"
        />
        {/* Progress circle */}
        <circle
          cx="32"
          cy="32"
          r="20"
          stroke={getScoreColor(score)}
          strokeWidth="4"
          fill="transparent"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{
            animation: 'dashoffset 1s ease-out forwards',
          }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-900">
        {score}
      </span>
    </div>
  )
}

// Category badge component
function CategoryBadge({ category, type }: { category: string; type: ScoreCardProps['type'] }) {
  const getStyle = (type: ScoreCardProps['type']) => {
    switch (type) {
      case 'awareness':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'sentiment':
        return 'bg-green-100 text-green-700 border-green-200'
      case 'credibility':
        return 'bg-purple-100 text-purple-700 border-purple-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStyle(type)}`}
    >
      {category}
    </span>
  )
}

export function ScoreCard({
  title,
  score,
  category,
  description,
  icon,
  type = 'awareness',
  className = '',
}: ScoreCardProps) {
  const hasScore = score !== undefined && score > 0

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-gray-200/50 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gray-300/60 hover:shadow-lg ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            {icon && <span className="text-gray-600">{icon}</span>}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          </div>

          {hasScore ? (
            <div className="flex items-center gap-4">
              <CircularProgress score={score} />
              <div className="flex-1">
                <div className="text-2xl font-bold text-gray-900">{score}/100</div>
                {category && <CategoryBadge category={category} type={type} />}
              </div>
            </div>
          ) : (
            category && (
              <div className="py-6">
                <CategoryBadge category={category} type={type} />
              </div>
            )
          )}

          {description && <p className="mt-3 line-clamp-2 text-xs text-gray-600">{description}</p>}
        </div>
      </div>

      {/* Subtle gradient background based on type */}
      <div
        className={`absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-10 ${
          type === 'awareness'
            ? 'bg-gradient-to-br from-blue-400 to-blue-600'
            : type === 'sentiment'
              ? 'bg-gradient-to-br from-green-400 to-green-600'
              : 'bg-gradient-to-br from-purple-400 to-purple-600'
        }`}
      />
    </div>
  )
}
